import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, TemplateRef } from '@angular/core';
import {
  IconComponent,
  IMAGES_PATH_TOKEN,
  ImagesPathProvider,
  PluralizeUnitPipe,
  TooltipDirective,
} from '@shared/ui-shared';
import { ServiceCode } from '../../models/service.code.model';

type ServiceCodeImage = Record<number | string, string>;

@Component({
  selector: 'csl-container-volume-option',
  standalone: true,
  imports: [NgOptimizedImage, TooltipDirective, CommonModule, IconComponent, PluralizeUnitPipe],
  templateUrl: './container-volume-option.component.html',
  styleUrl: './container-volume-option.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ImagesPathProvider],
})
export class ContainerVolumeOptionComponent {
  private imagePath = inject<string>(IMAGES_PATH_TOKEN);
  public serviceCode = input.required<ServiceCode>();
  public size = input.required<number>();
  public description = input<string | TemplateRef<unknown>>('');
  public unitOfMeasure = input<string>('');
  public name = input<string>('');
  public dimensions = input<string>('');
  public tip = input<string>('');
  public selected = input<boolean>(false);

  public readonly serviceCodes = ServiceCode;
  private readonly defaultImage = `${this.imagePath}/containers/residential/recycling-container.png`;
  private readonly containerImageMapping: Partial<{ [key in ServiceCode]: ServiceCodeImage }> = {
    [ServiceCode.TrashPickUpTownServiceDetailDto]: {
      48: `${this.imagePath}/containers/residential/trash-container.png`,
      64: `${this.imagePath}/containers/residential/trash-container.png`,
      96: `${this.imagePath}/containers/residential/trash-container.png`,
    },
    [ServiceCode.RecyclingTownServiceDetailDto]: {
      48: `${this.imagePath}/containers/residential/recycling-container.png`,
      64: `${this.imagePath}/containers/residential/recycling-container.png`,
      96: `${this.imagePath}/containers/residential/recycling-container.png`,
    },
    [ServiceCode.TrashPickUpAndRecyclingTownServiceDetailDto]: {
      48: `${this.imagePath}/containers/residential/trash-and-recycling-container.png`,
      64: `${this.imagePath}/containers/residential/trash-and-recycling-container.png`,
      96: `${this.imagePath}/containers/residential/trash-and-recycling-container.png`,
    },
    [ServiceCode.FoodWasteTownServiceDetailDto]: {
      6: `${this.imagePath}/containers/residential/food-waste-container-6.png`,
      32: `${this.imagePath}/containers/residential/food-waste-container-32.png`,
    },
    [ServiceCode.CommercialTrash]: {
      96: `${this.imagePath}/containers/residential/trash-container.png`,
      2: `${this.imagePath}/containers/commercial/trash-container.png`,
      4: `${this.imagePath}/containers/commercial/trash-container.png`,
      6: `${this.imagePath}/containers/commercial/trash-container.png`,
      8: `${this.imagePath}/containers/commercial/trash-container.png`,
      10: `${this.imagePath}/containers/commercial/trash-container.png`,
    },
    [ServiceCode.CommercialRecycling]: {
      96: `${this.imagePath}/containers/commercial/recycling-container2.png`,
      2: `${this.imagePath}/containers/commercial/recycling-container.png`,
      4: `${this.imagePath}/containers/commercial/recycling-container.png`,
      6: `${this.imagePath}/containers/commercial/recycling-container.png`,
      8: `${this.imagePath}/containers/commercial/recycling-container.png`,
      10: `${this.imagePath}/containers/commercial/recycling-container.png`,
    },
    [ServiceCode.CommercialFoodWaste]: {
      default: `${this.imagePath}/containers/commercial/food-container.png`,
    },
    [ServiceCode.RollOff]: {
      1: `${this.imagePath}/containers/roll-off-container.png`,
      2: `${this.imagePath}/containers/roll-off-compactor-container.png`,
    },
  };

  public get containerImage(): string {
    const sizeMap = this.containerImageMapping[this.serviceCode()];
    return sizeMap?.[this.size()] ?? sizeMap?.['default'] ?? this.defaultImage;
  }

  public get isSimpleType(): boolean {
    return typeof this.description() === 'string';
  }
}
